import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Fab from '@mui/material/Fab';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import ChecklistIcon from '@mui/icons-material/Checklist';
import PaymentsIcon from '@mui/icons-material/Payments';
import api, { errMsg } from '../api/client';
import { formatMoney } from './Money';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Plain-language status: "You have to pay ₹X", "Advance ₹X given — work due",
 * "Settled", or "No work yet" for a freshly added worker.
 */
const BalanceChip = ({ worker }) => {
  const { t } = useTranslation();
  const earned = Number(worker.earned) || 0;
  const paid = Number(worker.paid) || 0;
  const balance = earned - paid;

  if (earned === 0 && paid === 0) {
    return <Chip size="small" variant="outlined" label={t('worker.notStarted')} />;
  }
  if (balance > 0) {
    return <Chip size="small" color="error" variant="outlined" label={t('worker.toPay', { amt: formatMoney(balance) })} />;
  }
  if (balance < 0) {
    return <Chip size="small" color="warning" variant="outlined" label={t('worker.advanceWorkDue', { amt: formatMoney(-balance) })} />;
  }
  return <Chip size="small" color="success" variant="outlined" label={t('worker.settled')} />;
};

const AddWorkerDialog = ({ open, onClose, onSaved, projectId }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', phone: '', skill: '', dailyRate: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError('');
    setBusy(true);
    try {
      await api.post(`/api/projects/${projectId}/workers`, {
        name: form.name,
        phone: form.phone || undefined,
        skill: form.skill || undefined,
        dailyRate: form.dailyRate ? Number(form.dailyRate) : 0,
      });
      setForm({ name: '', phone: '', skill: '', dailyRate: '' });
      onSaved();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('worker.add')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label={t('worker.name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />
          <TextField label={t('worker.skill')} value={form.skill} onChange={(e) => setForm({ ...form, skill: e.target.value })} />
          <TextField
            label={t('worker.dailyRate')}
            value={form.dailyRate}
            onChange={(e) => setForm({ ...form, dailyRate: e.target.value.replace(/[^\d]/g, '') })}
            type="tel"
            InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          />
          <TextField
            label={t('worker.phone')}
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d]/g, '').slice(0, 10) })}
            type="tel"
            helperText={t('worker.phoneHelp')}
            InputProps={{ startAdornment: <InputAdornment position="start">+91</InputAdornment> }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!form.name.trim() || busy} onClick={submit}>
          {t('common.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PayWorkerDialog = ({ worker, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [type, setType] = useState('payment');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError('');
    setBusy(true);
    try {
      await api.post(`/api/workers/${worker.id}/payments`, {
        amount: Number(amount),
        type,
        date: today(),
        note: note || undefined,
      });
      onSaved();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!worker} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {t('worker.recordPayment')} — {worker?.name}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('expense.amount')}
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))}
            type="tel"
            autoFocus
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              style: { fontSize: 22, fontWeight: 700 },
            }}
          />
          <ToggleButtonGroup exclusive fullWidth value={type} onChange={(_, v) => v && setType(v)}>
            <ToggleButton value="payment">{t('worker.payment')}</ToggleButton>
            <ToggleButton value="advance">{t('worker.advance')}</ToggleButton>
          </ToggleButtonGroup>
          <TextField label={t('expense.note')} value={note} onChange={(e) => setNote(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!amount || busy} onClick={submit}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AttendanceDialog = ({ open, onClose, onSaved, projectId, workers }) => {
  const { t } = useTranslation();
  const [date, setDate] = useState(today());
  const [marks, setMarks] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const loadExisting = async (d) => {
    try {
      const { data } = await api.get(`/api/projects/${projectId}/attendance`, { params: { date: d } });
      const next = {};
      for (const row of data) next[row.project_worker_id] = row.status;
      setMarks(next);
    } catch {
      setMarks({});
    }
  };

  React.useEffect(() => {
    if (open) loadExisting(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, date]);

  const submit = async () => {
    setError('');
    setBusy(true);
    try {
      const entries = Object.entries(marks).map(([workerId, status]) => ({ workerId, status }));
      await api.post(`/api/projects/${projectId}/attendance`, { date, entries });
      onSaved();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('worker.markAttendance')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('expense.date')}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {workers.map((w) => (
            <Stack key={w.id} direction="row" alignItems="center" spacing={1}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {w.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ₹{Number(w.daily_rate)}/day{w.skill ? ` · ${w.skill}` : ''}
                </Typography>
              </Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={marks[w.id] || null}
                onChange={(_, v) => setMarks({ ...marks, [w.id]: v })}
              >
                <ToggleButton value="full" color="success">{t('worker.fullDay')}</ToggleButton>
                <ToggleButton value="half" color="warning">{t('worker.halfDay')}</ToggleButton>
                <ToggleButton value="absent" color="error">{t('worker.absent')}</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={busy || Object.values(marks).filter(Boolean).length === 0}
          onClick={submit}
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Workers list with balances + attendance & pay actions. access: 'owner' | 'worker' */
const WorkersTab = ({ projectId, workers, access, onChanged }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const [attOpen, setAttOpen] = useState(false);
  const [payWorker, setPayWorker] = useState(null);

  const isOwner = access === 'owner';

  return (
    <Box>
      {isOwner && workers.length > 0 && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={<ChecklistIcon />}
          onClick={() => setAttOpen(true)}
          sx={{ mb: 2 }}
        >
          {t('worker.markAttendance')} ({t('common.today')})
        </Button>
      )}

      {workers.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="subtitle1">{t('worker.empty')}</Typography>
            {isOwner && (
              <Typography variant="body2" color="text.secondary">
                {t('worker.emptyHint')}
              </Typography>
            )}
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {workers.map((w) => (
            <Card key={w.id}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: 'secondary.main', color: '#fff' }}>
                    {w.name[0]?.toUpperCase()}
                  </Avatar>
                  <Box
                    sx={{ flexGrow: 1, minWidth: 0, cursor: 'pointer' }}
                    onClick={() => navigate(`/workers/${w.id}`)}
                  >
                    <Typography variant="subtitle2" noWrap>
                      {w.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {w.skill ? `${w.skill} · ` : ''}₹{Number(w.daily_rate)}/day
                    </Typography>
                  </Box>
                  <BalanceChip worker={w} />
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <Button size="small" variant="text" onClick={() => navigate(`/workers/${w.id}`)}>
                    {t('worker.ledger')}
                  </Button>
                  {isOwner && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PaymentsIcon />}
                      onClick={() => setPayWorker(w)}
                    >
                      {t('worker.pay')}
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {isOwner && (
        <>
          <Fab
            color="primary"
            variant="extended"
            onClick={() => setAddOpen(true)}
            sx={{ position: 'fixed', bottom: 24, right: 24 }}
          >
            <AddIcon sx={{ mr: 0.5 }} /> {t('worker.add')}
          </Fab>
          <AddWorkerDialog
            open={addOpen}
            onClose={() => setAddOpen(false)}
            onSaved={() => {
              setAddOpen(false);
              onChanged();
            }}
            projectId={projectId}
          />
          <AttendanceDialog
            open={attOpen}
            onClose={() => setAttOpen(false)}
            onSaved={() => {
              setAttOpen(false);
              onChanged();
            }}
            projectId={projectId}
            workers={workers}
          />
          <PayWorkerDialog
            worker={payWorker}
            onClose={() => setPayWorker(null)}
            onSaved={() => {
              setPayWorker(null);
              onChanged();
            }}
          />
        </>
      )}
    </Box>
  );
};

export default WorkersTab;
